import { Link } from 'react-router-dom';
import { ArrowRight, BadgeCheck, Phone, Star } from 'lucide-react';
import { SERVICE_CATALOG, SUPPORT_PHONE, SUPPORT_PHONE_DISPLAY } from '@/lib/domain';
import styles from './Landing.module.css';

const TRUST = [
  {
    title: 'Prestataires vérifiés',
    text: 'Pièce d’identité contrôlée et candidature validée par notre équipe avant toute mission.',
    image: '/assets/illustrations/verified.png',
  },
  {
    title: 'Propositions au choix',
    text: 'Publiez votre demande, les prestataires vous envoient leur prix, vous choisissez.',
    image: '/assets/illustrations/money.png',
  },
  {
    title: 'Suivi et support',
    text: 'Chat, photos avant et après, avis final, et un support joignable par téléphone.',
    image: '/assets/illustrations/support.png',
  },
];

const STEPS = [
  {
    title: 'Réservez un service à domicile',
    text: 'Demandez un service en quelques clics avec tous les détails de votre besoin.',
    image: '/assets/illustrations/clipboard.png',
  },
  {
    title: 'Comparez les propositions',
    text: 'Les prestataires vérifiés vous envoient leur prix. Vous choisissez celui qui vous convient.',
    image: '/assets/illustrations/handshake.png',
  },
  {
    title: 'Suivez votre demande en temps réel',
    text: 'Communiquez avec votre prestataire et suivez l’avancement de votre service.',
    image: '/assets/illustrations/chat.png',
  },
];

/* Illustrative content for the example card only: not real providers or prices. */
const EXAMPLE_OFFERS = [
  { name: 'Samira B.', rating: '4,9', price: 250 },
  { name: 'Youssef A.', rating: '4,8', price: 220 },
  { name: 'Karim E.', rating: '5,0', price: 280 },
];

function ExampleRequest() {
  return (
    <figure className={styles.example} aria-label="Exemple de demande avec trois propositions">
      <div className={styles.exampleHead}>
        <span className={styles.exampleArt} style={{ background: 'var(--svc-nettoyage)' }}>
          <img src="/assets/illustrations/nettoyage.png" alt="" />
        </span>
        <div className={styles.exampleTitle}>
          <strong>Nettoyage</strong>
          <span>Demain · 10:00</span>
        </div>
        <span className={styles.exampleTag}>Exemple</span>
      </div>

      <p className={styles.exampleCount}>
        <span className={styles.livePulse} aria-hidden="true" />3 propositions reçues
      </p>

      <ul className={styles.offerList}>
        {EXAMPLE_OFFERS.map((offer, index) => (
          <li key={offer.name} className={styles.offer} style={{ animationDelay: `${0.5 + index * 0.7}s` }}>
            <span className={styles.offerAvatar}>{offer.name[0]}</span>
            <div className={styles.offerInfo}>
              <strong>
                {offer.name}
                <BadgeCheck size={15} className={styles.offerCheck} aria-label="Vérifié" />
              </strong>
              <span>
                <Star size={12} fill="#FFB21A" color="#FFB21A" /> {offer.rating}
              </span>
            </div>
            <span className={`${styles.offerPrice} num`}>{offer.price} MAD</span>
          </li>
        ))}
      </ul>

      <figcaption className={styles.exampleFoot}>Vous choisissez, le prestataire confirme.</figcaption>
    </figure>
  );
}

export default function Landing() {
  return (
    <div className={styles.page}>
      <header className={styles.topbar}>
        <Link to="/" className={styles.brand} aria-label="DABA, accueil">
          <img src="/assets/brand/logo.png" alt="DABA" />
        </Link>
        <nav className={styles.topActions} aria-label="Compte">
          <Link className={styles.loginLink} to="/login">
            Se connecter
          </Link>
          <Link className="btn btn--navy btn--sm" to="/signup">
            Créer un compte
          </Link>
        </nav>
      </header>

      <section className={styles.hero}>
        <div className={styles.heroInner}>
          <div className={styles.heroCopy}>
            <h1 className={styles.heroTitle}>Trouvez un professionnel fiable, chez vous.</h1>
            <p className={styles.heroText}>
              Découvrez une sélection de prestataires vérifiés pour vos services à domicile.
            </p>
            <div className={styles.heroActions}>
              <Link className={`btn btn--on-brand ${styles.heroPrimary}`} to="/signup">
                Trouver un prestataire <ArrowRight size={18} />
              </Link>
              <Link className={`btn ${styles.heroSecondary}`} to="/signup?type=provider">
                Devenir prestataire
              </Link>
            </div>
          </div>

          <div className={styles.exampleHero}>
            <ExampleRequest />
          </div>
        </div>
      </section>

      <section className={styles.services} aria-labelledby="services-title">
        <h2 id="services-title" className={styles.servicesTitle}>
          Quel service vous faut-il ?
        </h2>
        <ul className={styles.serviceGrid}>
          {SERVICE_CATALOG.map((service) => (
            <li key={service.id}>
              <Link className={styles.serviceTile} to="/signup">
                <span className={styles.serviceArt} style={{ background: service.tint }}>
                  <img src={service.image} alt="" />
                </span>
                <span className={styles.serviceName}>{service.name}</span>
                <ArrowRight size={18} className={styles.serviceArrow} />
              </Link>
            </li>
          ))}
        </ul>
      </section>

      {/* On phones the example follows the services so they stay in the first screen. */}
      <div className={styles.exampleBelow}>
        <ExampleRequest />
      </div>

      <section className={styles.trust} aria-label="Pourquoi DABA">
        {TRUST.map((item) => (
          <div key={item.title} className={styles.trustItem}>
            <img className={styles.trustArt} src={item.image} alt="" />
            <div>
              <h3 className={styles.trustTitle}>{item.title}</h3>
              <p className={styles.trustText}>{item.text}</p>
            </div>
          </div>
        ))}
      </section>

      <section className={styles.steps} aria-labelledby="steps-title">
        <h2 id="steps-title" className={styles.sectionTitle}>
          Comment ça marche
        </h2>
        <ol className={styles.stepList}>
          {STEPS.map((step, index) => (
            <li key={step.title} className={styles.step}>
              <span className={styles.stepArt}>
                <img src={step.image} alt="" />
                <span className={styles.stepNum} aria-hidden="true">
                  {index + 1}
                </span>
              </span>
              <h3 className={styles.stepTitle}>{step.title}</h3>
              <p className={styles.stepText}>{step.text}</p>
            </li>
          ))}
        </ol>
        <Link className="btn btn--primary" to="/signup">
          Publier ma première demande
        </Link>
      </section>

      <section className={styles.pro}>
        <img className={styles.proArt} src="/assets/illustrations/toolbox.png" alt="" />
        <div className={styles.proCopy}>
          <h2 className={styles.proTitle}>Vous êtes prestataire ?</h2>
          <p className={styles.proText}>
            Recevez des demandes de clients, envoyez votre prix et développez votre activité.
          </p>
        </div>
        <Link className="btn btn--primary" to="/signup?type=provider">
          Devenir prestataire
        </Link>
      </section>

      <footer className={styles.footer}>
        <div className={styles.footerBrand}>
          <img src="/assets/brand/logo.png" alt="DABA" />
          <p>Services à domicile, partout au Maroc.</p>
        </div>
        <a className={styles.footerPhone} href={`tel:${SUPPORT_PHONE}`}>
          <Phone size={16} /> Support : {SUPPORT_PHONE_DISPLAY}
        </a>
        <p className={styles.footerLinks}>
          Déjà inscrit ? <Link to="/login">Connectez-vous</Link> · Nouveau ?{' '}
          <Link to="/signup">Créer un compte</Link>
        </p>
      </footer>
    </div>
  );
}
